import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Carousel } from 'react-responsive-carousel';
import {
  Box,
  Dialog,
  FormControl,
  IconButton,
  OutlinedInput,
  Select,
  Slide,
  Typography,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { getAdmins, inviteAdmin, updateAdmin } from 'src/store/reducers/adminsSlice';
import { getStudios } from 'src/store/reducers/studiosSlice';
import { generalStyles } from 'src/utils/generalStyles/generalStyles';
import { isEmail, isName } from 'src/utils/validation';

import { ChevronDown, ChevronUp, CloseIcon } from '../../../Icons';
import { closeAdminDialog, resetAdminDialogParams } from '../../../store/reducers/dialogSlice';
import { closeModal, showModal } from '../../../store/reducers/modalSlice';
import { SubmitButton } from '../../Button/SubmitButton';
import { AvatarSelect } from '../../ImageSelect/AvatarSelect';
import { DefaultInput } from '../../Input/DefaultInput';
import { Steppler } from '../../Steppler/Steppler';

import { SelectStudioMenuItem } from './SelectStudioMenuItem';

const Transition = React.forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
  ) => {
    return (
      <Slide
        direction="up"
        ref={ref}
        {...props}
      />
    );
  },
);

const steps = [
  {
    title: 'Basic information',
  },
  {
    title: 'Assign',
  },
];

export const AdminDialog = ({ isOpen }: any) => {
  const dispatch = useDispatch();

  const { adminDialogParams: params } = useSelector((state: any) => state.dialog);
  const { studios } = useSelector((state: any) => state.studios);

  const [activeStep, setActiveStep] = useState<number>(params.step);
  const [maxAvailableStepToClick, setMaxAvailableStepToClick] = useState<number>(params.maxAvailableStep);
  const formRef = useRef<any>();
  const avatarRef = useRef<any>();

  // Step 1 values
  const [photo, setPhoto] = useState<string>(params.data.photo);
  const [firstName, setFirstName] = useState<string>(params.data.firstName);
  const [lastName, setLastName] = useState<string>(params.data.lastName);
  const [email, setEmail] = useState<string>(params.data.email);

  // Step 2 values
  const studiosToSelect = useMemo(() => [
    {
      id: '0',
      title: 'Without Studio',
      images: [],
    },
    ...studios,
  ], [studios]);
  const [assignedStudios, setAssignedStudios] = React.useState<string[]>(params.data.studios.map(({ id }: any) => id));
  const [isSelectStudiosOpen, setIsSelectStudiosOpen] = useState(false);
  const { isUpdateRequestLoading, isInviteRequestLoading } = useSelector((state: any) => state.admins);

  const handleChangeName = useCallback((newValue: string) => {
    setFirstName(newValue);
  }, []);

  const handleChangeSurname = useCallback((newValue: string) => {
    setLastName(newValue);
  }, []);

  const handleChangeEmail = useCallback((newValue: string) => {
    setEmail(newValue);
  }, []);

  const handleChangeImage = useCallback((newImage: string) => {
    setPhoto(newImage);
  }, []);

  const isContinueButtonAvailable = useMemo(() => {
    return isName(firstName) && isName(lastName) && isEmail(email) && photo !== '';
  }, [firstName, email, photo, lastName]);

  const isSaveButtonValid = useMemo(() => {
    return isContinueButtonAvailable;
  }, [isContinueButtonAvailable]);

  const handleChangeStep = (nextStep: number) => {
    setActiveStep(nextStep);
  };

  const handleContinueButtonPress = () => {
    setActiveStep(oldStep => oldStep + 1);
    setMaxAvailableStepToClick(activeStep + 1);
  };

  const resetDialogData = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setAssignedStudios([]);
    setActiveStep(0);
    setMaxAvailableStepToClick(0);

    formRef?.current?.reset();
    avatarRef?.current?.deleteImage();
  };

  const onClose = () => {
    resetDialogData();
    dispatch(resetAdminDialogParams());
    dispatch(closeAdminDialog());
  };

  const handleSaveButtonPress = () => {
    if (params.isEdit) {
      dispatch(
        updateAdmin({
          id: params.data.id,
          firstName: params.data.firstName !== firstName ? firstName : undefined,
          lastName: params.data.lastName !== lastName ? lastName : undefined,
          email: params.data.email !== email ? email : undefined,
          photo: params.data.photo !== photo ? photo : undefined,
          studios: params.data.studios !== assignedStudios ? assignedStudios : undefined,
          onSuccess: () => {
            dispatch(
              showModal({
                modalType: 'SUCCESS',
                successText: 'Changes successfully saved',
                okButtonTitle: 'Ok, I got it',
                onButtonClick: () => {
                  dispatch(getAdmins());
                  dispatch(closeModal());
                  onClose();
                },
              }),
            );
          },
          onFailure: (error: any) => {
            dispatch(
              showModal({
                modalType: 'DELETE',
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: 'Ok, I got it',
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              }),
            );
          },
        }),
      );
    } else {
      dispatch(
        inviteAdmin({
          firstName,
          lastName,
          email,
          photo,
          studios: assignedStudios.length > 0 && assignedStudios[0] !== '0' ? assignedStudios : undefined,
          onSuccess: () => {
            dispatch(
              showModal({
                modalType: 'SUCCESS',
                successText: 'Admin successfully added',
                okButtonTitle: 'Ok, I got it',
                onButtonClick: () => {
                  dispatch(getAdmins());
                  dispatch(closeModal());
                  onClose();
                },
              }),
            );
          },
          onFailure: (error: any) => {
            dispatch(
              showModal({
                modalType: 'DELETE',
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: 'Ok, I got it',
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              }),
            );
          },
        }),
      );
    }
  };

  const handleChangeSelectStudio = useCallback(({ target: { value } }: any) => {
    const withoutStudioId = studiosToSelect[0].id;

    if (value.length > 1 && value[value.length - 1] === withoutStudioId) {
      setAssignedStudios([withoutStudioId]);
    } else if (value.length > 1 && value[0] === withoutStudioId) {
      setAssignedStudios(value.filter((id: string) => id !== withoutStudioId));
    } else {
      setAssignedStudios(value);
    }
  },[studiosToSelect]);

  useEffect(() => {
    dispatch(getStudios());
  }, []);

  useEffect(() => {
    // todo little hack for scroll to top after every change active step carousel
    //@ts-ignore
    window.document.querySelector('.MuiPaper-root')?.scrollTo(0, 0);
  }, [activeStep]);

  return (
    <Dialog
      fullScreen
      open={isOpen}
      onClose={onClose}
      disableEscapeKeyDown
      TransitionComponent={Transition}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          width: '100%',
          backgroundColor: '#fafafa',
          padding: '16px 40px',
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            width: '100%',
            marginBottom: '16px',
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              backgroundColor: '#F3F3F3',
              color: '#676767',
              fontSize: '26px',
            }}
            disableRipple
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
          <Box>
            <Typography
              sx={{
                fontSize: '26px',
                fontWeight: '600',
              }}
            >
              {params.isEdit ? 'Edit admin' : 'Add new admin'}
            </Typography>
          </Box>

          {activeStep === 0 ? (
            <SubmitButton
              title="Continue"
              disabled={!isContinueButtonAvailable}
              onClick={handleContinueButtonPress}
            />
          ) : (
            <SubmitButton
              title={params.isEdit ? 'Save changes' : 'Create admin'}
              isLoading={params.isEdit ? isUpdateRequestLoading : isInviteRequestLoading}
              disabled={!isSaveButtonValid}
              onClick={handleSaveButtonPress}
            />
          )}
        </Box>

        <Steppler
          steps={steps}
          maxAvailableStepToClick={maxAvailableStepToClick}
          activeStep={activeStep}
          onStepChange={handleChangeStep}
        />
      </Box>
      <Carousel
        selectedItem={activeStep}
        showArrows={false}
        showStatus={false}
        showIndicators={false}
        showThumbs={false}
        transitionTime={120}
      >
        {/* Step 1 = Basic Information*/}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              padding: '96px 16px 0',
              width: '761px',
              minWidth: '400px',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '20px',
                  fontWeight: '600',
                  marginBottom: '48px',
                }}
              >
                Basic information
              </Typography>
              <AvatarSelect
                ref={avatarRef}
                image={photo}
                onImageChanged={handleChangeImage}
              />
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: '400',
                  color: '#828282',
                  marginTop: '16px',
                  marginBottom: '40px',
                }}
              >
                Allowed file types : png, jpg, jpeg.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
              }}
            >
              <form ref={formRef}>
                <DefaultInput
                  initialValue={firstName}
                  placeholder="name"
                  label="Name"
                  validationFn={isName}
                  onInputValueChange={handleChangeName}
                  sx={generalStyles.borderTopRightLeftRadius}
                />
                <DefaultInput
                  initialValue={lastName}
                  placeholder="surname"
                  label="Surname"
                  validationFn={isName}
                  onInputValueChange={handleChangeSurname}
                  sx={generalStyles.borderTopNone}
                />
                <DefaultInput
                  initialValue={email}
                  placeholder="email address"
                  label="Email"
                  onInputValueChange={handleChangeEmail}
                  validationFn={isEmail}
                  sx={generalStyles.borderBottomLeftRightRadius}
                />
              </form>
            </Box>
          </Box>
        </Box>
        {/* Step 2 = Basic Information*/}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              padding: '96px 16px 0',
              width: '761px',
              minWidth: '400px',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '20px',
                  fontWeight: '600',
                  marginBottom: '48px',
                }}
              >
                Assign
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
              }}
            >
              <Box
                sx={{
                  padding: '16px',
                  minWidth: 192,
                  backgroundColor: '#F3F3F3',
                  color: '#1F1F1F',
                  border: '1px solid #D6D6DB',
                  borderRight: 'none',
                  borderTopLeftRadius: '6px',
                  borderBottomLeftRadius: '6px',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: '500',
                    textTransform: 'capitalize',
                  }}
                >
                  studio
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flex: 1,
                  border: '1px solid #D6D6DB',
                  borderLeft: 'none',
                  borderTopRightRadius: '6px',
                  borderBottomRightRadius: '6px',
                  ...(isSelectStudiosOpen && {
                    border: 'none',
                    borderRadius: 0,
                  }),
                }}
              >
                <FormControl sx={{ width: '100%', maxWidth: '535px' }}>
                  <Select
                    labelId="demo-multiple-checkbox-label"
                    id="demo-multiple-checkbox"
                    placeholder="Select input"
                    multiple
                    value={assignedStudios}
                    onChange={handleChangeSelectStudio}
                    input={<OutlinedInput />}
                    IconComponent={() => (
                      <>{isSelectStudiosOpen ? <ChevronUp fontSize="inherit" /> : <ChevronDown fontSize="inherit" />}</>
                    )}
                    onOpen={() => setIsSelectStudiosOpen(true)}
                    onClose={() => setIsSelectStudiosOpen(false)}
                    renderValue={value => {
                      return value?.length > 0
                        ? value.map(id => studiosToSelect.find((el: any) => el.id === id)?.title).join('; ')
                        : 'Select studio';
                    }}
                    displayEmpty
                    sx={{
                      borderRadius: '0px',
                      color: '#828282',
                      fontWeight: '400',
                      height: '56px',

                      '& .MuiSvgIcon-root': {
                        fontSize: '24px',
                        marginRight: '16px',
                        color: '#828282',
                      },
                      boxShadow: 'none',

                      ...(isSelectStudiosOpen && {
                        border: 'none',
                        borderRadius: 0,
                        borderTopRightRadius: '2px',
                        borderTopLeftRadius: '2px',
                        boxShadow: 'rgb(0, 0, 0, 0.1) 0px 0px 3px 0px',
                      }),

                      '.MuiOutlinedInput-notchedOutline': { border: 0 },
                      '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                        border: 0,
                      },
                      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 0,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: 0,
                          boxShadow: 'rgb(0, 0, 0, 0.1) 0px 3px 3px 0px',

                          '& .MuiList-root': {
                            padding: 0,
                          },

                          '& .MuiMenuItem-root': {
                            minWidth: 120,
                            color: '#1F1F1F',
                            fontSize: '14px',
                            height: '56px',
                            margin: '0',
                            padding: '16px',
                            border: 'none',
                            '&:hover': {
                              backgroundColor: '#FFF8FA',

                              '& .MuiCheckbox-root': {
                                backgroundColor: 'white',
                                borderColor: '#BF295C',
                                boxShadow: '#FFF2F7 0px 0px 0px 4px',
                              },

                              '& .Mui-checked': {
                                backgroundColor: 'transparent',
                              },
                            },
                            '&.Mui-selected, &.Mui-selected:hover': {
                              backgroundColor: 'transparent',
                            },
                          },
                        },
                      },
                    }}
                  >
                    {studiosToSelect.map(({ title, images, id }: any) => (
                      <SelectStudioMenuItem
                        key={id}
                        value={id}
                        checked={!!~assignedStudios.indexOf(id)}
                        imageSrc={images?.[0]}
                        title={title}
                      />
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
        </Box>
      </Carousel>
    </Dialog>
  );
};
